import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving formatting
 * @param htmlString - The HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 */
export const sanitizeHtml = (htmlString: string): string => {
  if (!htmlString) return '';

  // Configure DOMPurify to allow specific tags and attributes
  const config = {
    ALLOWED_TAGS: ['p', 'strong', 'em', 'b', 'i', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'br', 'div', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['style', 'class'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    // Allow font-family and font-size in style attributes
    ALLOW_DATA_ATTR: false,
  };

  // Sanitize the HTML
  return DOMPurify.sanitize(htmlString, config);
};

/**
 * Detects if a string contains HTML tags
 * @param content - The content to check
 * @returns true if HTML tags are detected
 */
export const hasHtmlTags = (content: string): boolean => {
  if (!content) return false;
  // Check for HTML tags - be more specific to avoid false positives
  // Look for common HTML tags that would indicate formatted content
  const htmlTagPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlTagPattern.test(content);
};

/**
 * Converts plain text to HTML by preserving newlines
 * @param text - Plain text content
 * @returns HTML string with newlines converted to <br> tags
 */
export const plainTextToHtml = (text: string): string => {
  if (!text) return '';
  
  // Normalize line endings (handle both \r\n and \n)
  const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  // Split by newlines and convert to HTML with <br> tags
  const lines = normalizedText.split('\n');
  const htmlContent = lines
    .map((line, index) => {
      // Escape HTML entities to prevent XSS
      const escapedLine = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      // Add <br> after each line except the last one
      // This preserves blank lines as well
      if (index < lines.length - 1) {
        return escapedLine + '<br />';
      }
      return escapedLine;
    })
    .join('');
  
  // Return the content directly - the parent Box will handle rendering
  return htmlContent;
};

/**
 * Renders content safely, handling both plain text and HTML
 * @param content - Content that may be plain text or HTML
 * @returns Sanitized HTML string ready for dangerouslySetInnerHTML
 */
export const renderFormattedContent = (content: string): string => {
  if (!content) return '';

  // Check if content has HTML tags
  const isHtml = hasHtmlTags(content);
  
  // If it's plain text, convert newlines to <br> tags
  if (!isHtml) {
    const converted = plainTextToHtml(content);
    return converted;
  }

  // If it's HTML, sanitize it
  return sanitizeHtml(content);
};

